import _ from 'underscore';
import utils = require('../utils')
import {ComparisonOperator, TOperator} from "./ComparisonOperator";
import {ArithmeticOperator} from "./ArithmeticOperator";
import {XmlTagInfo} from "../xmlTagProvider";
import {QuestionaryState} from "../questionary";
import {isEmpty, XmlUtils} from "../utils";

/*

 Sample:

 <expression>
 <compare operator="GT">
 <elapsedTime/>
 <literal>5000</literal>
 </compare>
 </expression>

 <expression>
 <compare operator="GT">
 <sum>
 <questionaryVariable variable="nQ02_7_0"/>
 <questionaryVariable variable="nQ03_7_0"/>
 <questionaryVariable variable="nQ04_7_0"/>
 <questionaryVariable variable="nQ05_7_0"/>
 </sum>
 <literal>2</literal>
 </compare>
 </expression>

 */


/**
 * Undefined safe add
 */
function add(a:number|undefined, b:number|undefined): number|undefined {
    return (a === undefined || b === undefined) ? undefined : a + b;
}
/**
 * Logical AND that returns undefined if result cannot be computed
 */
function and(a:boolean|undefined, b:boolean|undefined) {
    if (a === false || b === false) {
        return false;
    }
    return (a === undefined || b === undefined) ? undefined : a && b;
}
/**
 * Logical OR that returns undefined if result cannot be computed
 */
function or(a:boolean|undefined, b:boolean|undefined) {
    if (a === true || b === true) {
        return true;
    }
    return (a === undefined || b === undefined) ? undefined : a || b;
}
/**
 * Logical NOT that returns undefined if parameter is undefined.
 */
function not(a:boolean|undefined) {
    return (a === undefined) ? undefined : !a;
}


function toBool(value:any|undefined) {
    return _.isUndefined(value) ? undefined : (value !== 0 && value !== false && value !== null);
}
function addPrefixIfNotPresent(prefix: string, key: string) {
    return key.indexOf('.') === -1 ? prefix + '.' + key : key;
}

function readVariable(state: QuestionaryState, variableName: string): any {
    const variable = state.variables[variableName];
    if (!variable) {
        throw new Error(`Cannot read variable '${variableName}', it does not exist.`);
    }
    return variable();
}

function writeVariable(state: QuestionaryState, variableName: string, value: any): any {
    const variable = state.variables[variableName];
    if (!variable) {
        throw new Error(`Cannot write variable '${variableName}', it does not exist.`);
    }
    variable(value);
}

export interface Action extends XmlTagInfo {
    action: string;
}

export abstract class Expression<T> {
    dependencies: string [] = [];

    evaluate(state: QuestionaryState): T|undefined {
        return undefined;
    }

    init(state: QuestionaryState): void {
    }

    abstract describe(): string;

    protected aggregateDependencies(from: Expression<T>[]): string[] {
        return _.reduce(from, (result: string[], f: Expression<T>) => result.concat(f.dependencies), []);
    }
}
abstract class SyntheticExpression extends Expression<any> {
}

class RandomCheck extends SyntheticExpression {

    constructor(private count: number, private variableNames: string[]) {
        super();
    }

    init(state:QuestionaryState) {
        // set random just to empty variables
        const allVarsAreEmpty = _.every(this.variableNames, v => _.isUndefined(readVariable(state, v)));
        if (allVarsAreEmpty) {
            const shuffled: string[] = _.shuffle(this.variableNames);
            _.each(_.first(shuffled, this.count), (v: string) => writeVariable(state, v, 1));
            _.each(_.rest(shuffled, this.count), (v: string) => writeVariable(state, v, 0));
        }
    }

    describe(): string {
        return `RandomCheck(${this.count} of ${this.variableNames.length})`;
    }
}
class RandomValue extends SyntheticExpression {
    private variables: string[];
    private unique: boolean;
    constructor(private min: number, private max: number, unique: boolean, private variableNames: string[]) {
        super();
        this.variables = variableNames;
        this.unique = unique;
    }

    init(state: QuestionaryState) {
        const allUndefined = _.every(this.variables, v => _.isUndefined(readVariable(state, v)));
        if (allUndefined) {

            if (this.unique && this.variables.length < this.max - this.min) {
                this.unique = false;
            }

            if (this.unique) {
                let shuffled = _.shuffle(_.range(this.min, this.max + 1));
                _.each(this.variables, (v: string) => writeVariable(state, v, shuffled.pop()));
            } else {
                _.each(this.variables, (v: string) => writeVariable(state, v, _.random(this.min, this.max)));
            }
        }
    }

    describe(): string {
        return `RandomValue(${this.min}, ${this.max}), Unique ${this.unique ? 'yes' : 'no'}`;
    }
}

class TrueValue extends SyntheticExpression {

    describe(): string {
        return "TrueValue";
    }

    evaluate(state: QuestionaryState): true {
        return true;
    }

}

class QuestionaryVariable extends Expression<any> {

    constructor(
        private variable: string,
        private readonly operator?: 'ISEMPTY',
    ) {
        super();
        this.dependencies = [variable];
    }

    evaluate(state: QuestionaryState) {
        const value = readVariable(state, this.variable);
        if (this.operator === 'ISEMPTY') {
            return isEmpty(value);
        }
        return value;
    }

    describe(): string {
        return this.variable;
    }
}

class QuestionaryVariableComparison extends Expression<boolean> {
    constructor(
        private readonly variable: string,
        private readonly value: string,
        private readonly operator: TOperator,
        private readonly respectUndefined: boolean,
    ) {
        super();
        if (!variable) {
            console.warn("Missing variable in expression. Probably is somewhere selected Labelcell instead Inputcell in FormBeans as event input. Please set breakpoint on this line and reload to see more details.")
        }
        this.dependencies = [variable];
    }

    evaluate(state: QuestionaryState) {
        const variableValue = readVariable(state, this.variable);
        if (this.operator === 'ISEMPTY') {
            return isEmpty(variableValue);
        }
        if(variableValue === null || _.isUndefined(variableValue)) {
            // if respectUndefined is not set, consider missing value as false
            return this.respectUndefined ? variableValue : false;
        }
        return ComparisonOperator.undefinedSafe(this.operator)(variableValue, this.value);
    }

    describe(): string {
        return `${this.variable} ${ComparisonOperator.describe(this.operator)} ${this.value}`;
    }
}

class RespondentVariableComparison extends Expression<boolean> {
    constructor(
        private readonly variable: string,
        private readonly value: string,
        private readonly operator: TOperator,
        private readonly respectUndefined: boolean
    ) {
        super();
    }

    evaluate(state: QuestionaryState) {
        const valueVariable = state.attributes[addPrefixIfNotPresent('respondent', this.variable)];
        if (this.operator === 'ISEMPTY') {
            return isEmpty(valueVariable);
        }
        if(valueVariable === null || _.isUndefined(valueVariable)) {
            // if respectUndefined is not set, consider missing value as false
            return this.respectUndefined ? valueVariable : false;
        }
        return ComparisonOperator.numericSafe(this.operator)(valueVariable, this.value);
    }

    describe(): string {
        return `RespondentVariable[${this.variable}] ${this.operator} ${this.value}`;
    }
}

abstract class NaryExpression<T> extends Expression<T> {
    constructor(protected children: Expression<T>[]) {
        super();
        this.dependencies = this.aggregateDependencies(this.children);
    }
}

class AndExpression extends NaryExpression<boolean> {
    evaluate(state: QuestionaryState) {
        return _.reduce(
            this.children,
            (result:boolean|undefined, child: Expression<boolean>) => and(result,toBool(child.evaluate(state))),
            true
        );
    }

    describe(): string {
        return this.children.map(e => e.describe()).join(' && ');
    }
}

class OrExpression extends NaryExpression<boolean> {
    evaluate(state: QuestionaryState) {
        return _.reduce(
            this.children,
            (result:boolean|undefined, child: Expression<boolean>) => or(result, toBool(child.evaluate(state))),
            false
        );
    }
    describe(): string {
        return this.children.map(e => e.describe()).join(' || ');
    }
}

class NotExpression extends NaryExpression<boolean> {
    evaluate(state: QuestionaryState) {
        return not(toBool(this.children[0].evaluate(state)));
    }
    describe(): string {
        return `!${this.children[0].describe()}`;
    }
}

class RoundValueExpression extends NaryExpression<number> {
    private readonly roundType;
    private readonly decimalPlaces;

    constructor(node: XmlTagInfo) {
        super(node.children);
        this.roundType = <'NEAREST'|'DOWN'|'UP'>node.attributes['roundType'];
        this.decimalPlaces = Number(node.attributes['decimalPlaces']);
    }

    evaluate(state: QuestionaryState): number | undefined {
        const value = this.children[0].evaluate(state);
        if (value === undefined) {
            return undefined;
        }
        const shift = Math.pow(10, this.decimalPlaces);
        const fnc = this.roundType === 'UP' ? Math.ceil : (this.roundType === 'DOWN' ? Math.floor : Math.round);

        return fnc(value*shift) / shift;
    }
    describe(): string {
        return `RoundValue(${this.roundType}, ${this.decimalPlaces}, ${this.children.map(e => e.describe()).join(', ')})`;
    }
}

class PatternCount extends NaryExpression<number> {
    private regex: string;

    constructor(node: XmlTagInfo) {
        super(node.children);
        this.regex = node.attributes['regex'];
        if (this.children.length !== 1) {
            // console.log("Compare expects one argument", node);
        }
    }

    evaluate(state: QuestionaryState): number {
        let value = this.children[0].evaluate(state);
        if (value !== undefined) {
            let matches = value !== null ? value.toString().match(new RegExp(this.regex, "g")) : null;
            if (matches !== null) {
                return matches.length;
            }
        }
        return 0;
    }

    describe(): string {
        return `PatternCount(${this.regex} of ${this.children[0].describe()})`;
    }
}

class CompareExpression extends NaryExpression<boolean> {
    private readonly operator: TOperator;

    constructor(node: XmlTagInfo) {
        super(node.children);
        this.operator = <TOperator>node.attributes['operator'];
        if (this.children.length !== 2) {
            // console.log("Compare expects two arguments", node);
        }
    }

    evaluate(state: QuestionaryState) {
        const o = ComparisonOperator.undefinedSafe(this.operator);
        return o(this.children[0].evaluate(state), this.children[1].evaluate(state));
    }

    describe(): string {
        return this.children.map(e => e.describe()).join(` ${ComparisonOperator.describe(this.operator)} `);
    }
}

class SumExpression extends NaryExpression<any> {

    constructor(children: Expression<any>[], private respectUndefined: boolean) {
        super(children);
    }

    evaluate(state: QuestionaryState) {
        return _.reduce(this.children, (result: any, getter: Expression<any>) => {
            const value = getter.evaluate(state);
            return (this.respectUndefined && _.isUndefined(value)) ? undefined : add(result, Number(value) || 0);
        }, 0);
    }
    describe(): string {
        return `Sum(${this.children.map(e => e.describe()).join(', ')})`;
    }
}

class ArithmeticExpression extends NaryExpression<number> {
    private operator: string;

    constructor(node: XmlTagInfo) {
        super(node.children);
        this.operator = node.attributes['operator'];
        if (this.children.length !== 2) {
            // console.log("Arithmetic expects two arguments", node);
        }
    }

    evaluate(state: QuestionaryState): number|undefined {
        return ArithmeticOperator.numericSafe(this.operator)(
            this.children[0].evaluate(state),
            this.children[1].evaluate(state)
        );
    }

    describe(): string {
        return this.children.map(e => e.describe()).join(` ${ArithmeticOperator.describe(this.operator)} `);
    }
}

function syntheticHandlers(timeVariableName: string, variableNames: string[], respectUndefined: boolean) {
    return {
        literal: (node: XmlTagInfo) => new class extends SyntheticExpression {
            evaluate() {
                return node.value;
            }

            describe(): string {
                return node.value || '';
            }
        },
        questionaryVariable: (node: XmlTagInfo) => new QuestionaryVariable(node.attributes['variable']),
        elapsedTime: () => new QuestionaryVariable(timeVariableName),
        sum: (node: XmlTagInfo) => new SumExpression(node.children, respectUndefined),
        compare: (node: XmlTagInfo) => new CompareExpression(node),
        randomCheck: (node: XmlTagInfo) => new RandomCheck(parseInt(node.attributes['count'], 10), variableNames),
        randomValue: (node: XmlTagInfo) => new RandomValue(parseInt(node.attributes['min'], 10), parseInt(node.attributes['max'], 10), node.attributes['unique'] === 'yes', variableNames),
        patternCount: (node: XmlTagInfo) => new PatternCount(node),
        arithmetic: (node: XmlTagInfo) => new ArithmeticExpression(node),
        roundValue: (node: XmlTagInfo) => new RoundValueExpression(node),
    }
}


export abstract class EventAction {
    public abstract describe(): string;
}

export class CellAction extends EventAction {
    public action: string;
    public items: string[];

    constructor(action: string, items: string[]) {
        super();
        this.action = action;
        this.items = items;
    }

    public describe(): string {
        return `CellAction(${this.action}, ${this.items.join(', ')})`;
    }
}

export class SubmitForm extends EventAction {

    public describe(): string {
        return 'SubmitForm';
    }
}

export class Event {
    public condition: Expression<boolean>;
    public action: EventAction;

    constructor(condition: Expression<boolean>, action: EventAction) {
        this.condition = condition;
        this.action = action;
    }

    public describe(): string {
        return `${this.condition.describe()} => ${this.action.describe()}`
    }
}

export class Filter {
    public condition: Expression<boolean>;
    public action: FilterAction;

    constructor(condition: Expression<boolean>, action: FilterAction) {
        this.condition = condition;
        this.action = action;
    }

    public describe(): string {
        return `${this.condition.describe()} => ${this.action.describe()}`
    }
}


export abstract class FilterAction {
    constructor(public name: string) {
    }

    public describe(): string {
        return this.name;
    }
}

export module FilterAction {
    export class SkipForm extends FilterAction { constructor() { super('skipForm'); } }
    export class GotoForm extends FilterAction { constructor(public target: string) { super('gotoForm'); } }
    export class GotoEnd extends FilterAction { constructor() { super('gotoEnd'); } }
    export class HardScreenOut extends FilterAction { constructor() { super('hardScreenOut'); } }
    export class QualityCheckOut extends FilterAction { constructor() { super('qualityCheckOut'); } }
}

export class ExpressionParser {
    private static logicHandlers = {
        and: (node: XmlTagInfo) => new AndExpression(node.children),
        or: (node: XmlTagInfo) => new OrExpression(node.children),
        not: (node: XmlTagInfo) => new NotExpression(node.children),
        'true': () => new TrueValue(),
    };
    private static eventHandlers = _.extend({
        questionaryVariable: (node: XmlTagInfo) => new QuestionaryVariableComparison(node.attributes['variable'], node.attributes['value'], <TOperator>node.attributes['operator'],  false,),
        respondentVariable: (node: XmlTagInfo) => new RespondentVariableComparison(node.attributes['variable'], node.attributes['value'], <TOperator>node.attributes['operator'], false),
        visitAttribute: (node: XmlTagInfo) => new RespondentVariableComparison(node.attributes['variable'], node.attributes['value'], <TOperator>node.attributes['operator'], false)
    }, ExpressionParser.logicHandlers);
    private static eventActionHandlers = {
        cellAction: (node: XmlTagInfo) => new CellAction(node.attributes['action'], XmlUtils.childrenByName(node, "item").map(n => n.attributes['id'])),
        submitForm: (node: XmlTagInfo) => new SubmitForm()
    };
    private static filterHandlers = ExpressionParser.eventHandlers;
    private static filterActionHandlers = {
            skipForm: (node: XmlTagInfo) => new FilterAction.SkipForm(),
            gotoForm: (node: XmlTagInfo) => new FilterAction.GotoForm(node.attributes['target']),
            gotoEnd: (node: XmlTagInfo) => new FilterAction.GotoEnd(),
            hardScreenOut: (node: XmlTagInfo) => new FilterAction.HardScreenOut(),
            qualityCheckOut: (node: XmlTagInfo) => new FilterAction.QualityCheckOut()
    };

    private static quotaHandlers = _.extend({
        questionaryVariable: (node: XmlTagInfo) => new QuestionaryVariableComparison(node.attributes['variable'], node.attributes['value'], <TOperator>node.attributes['operator'],true),
        respondentVariable: (node: XmlTagInfo) => new RespondentVariableComparison(node.attributes['variable'], node.attributes['value'], <TOperator>node.attributes['operator'], true)
    }, ExpressionParser.logicHandlers);


    public static parseSynthetic(node: XmlTagInfo, timeVariableName: string, variableNames: string[], respectUndefined: boolean): SyntheticExpression {
        const handlers = _.extend(syntheticHandlers(timeVariableName, variableNames, respectUndefined), ExpressionParser.logicHandlers);
        return utils.traverse(node.children, handlers)[0];
    }

    public static parseEvent(node: XmlTagInfo): Event {
        const condition = utils.traverse(XmlUtils.childByName(node, 'condition').children,ExpressionParser.eventHandlers)[0];
        const action = utils.traverse(XmlUtils.childByName(node, 'action').children, this.eventActionHandlers)[0];
        return new Event(condition, action);
    }

    public static parseFilter(node: XmlTagInfo): Filter {
        const condition = utils.traverse(XmlUtils.childByName(node, 'condition').children, ExpressionParser.filterHandlers)[0];
        const action = utils.traverse(XmlUtils.childByName(node, 'action').children, ExpressionParser.filterActionHandlers)[0];
        return new Filter(condition, action);
    }

    public static parseQuota(node: XmlTagInfo) {
        const eCondition = XmlUtils.childByName(node, 'condition');
        if( !eCondition ) {
            // Complement cells don't have condition
            return undefined;
        } else {
            const condition = utils.traverse(eCondition.children, ExpressionParser.quotaHandlers)[0];
            return (state: QuestionaryState) : boolean => condition.evaluate(state);
        }
    }
}

export module ExpressionParser {
    export class PreviewType {
        public static NONE = 'NONE';
        public static CLICK_TO_FULL_IMAGE = 'CLICK_TO_FULL_IMAGE';
    }
}